/* PastéBCN — Shipping slider
   Web Component <pb-shipping-slider>:
   - IntersectionObserver para animación de entrada (.is-visible)
   - Flechas desktop con scrollBy(cardWidth + gap)
   - Counter actualizado vía IntersectionObserver dentro del track
   - Disabled en flechas cuando se llega al inicio/fin
   - Teclado: ← → en el track
   - Respeta prefers-reduced-motion */

if (!customElements.get('pb-shipping-slider')) {
  class PbShippingSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pb-shipping__track');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.pb-shipping__slide'));
      this.total = this.slides.length;

      this.prevBtn = this.querySelector('.pb-shipping__arrow--prev');
      this.nextBtn = this.querySelector('.pb-shipping__arrow--next');
      this.counterCurrent = this.querySelector('.pb-shipping__counter-current');
      this.counterTotal = this.querySelector('.pb-shipping__counter-total');

      if (this.counterTotal) {
        this.counterTotal.textContent = String(this.total).padStart(2, '0');
      }

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._observers = [];
      this._listeners = [];

      this._bindControls();
      this._observeEntry();
      this._observeSlides();
      this._updateArrowStates();
    }

    disconnectedCallback() {
      this._observers.forEach((o) => o.disconnect());
      this._listeners.forEach((fn) => fn());
      this._observers = [];
      this._listeners = [];
    }

    _bindControls() {
      if (this.prevBtn) {
        const handler = () => this._scrollByCard(-1);
        this.prevBtn.addEventListener('click', handler);
        this._listeners.push(() => this.prevBtn.removeEventListener('click', handler));
      }
      if (this.nextBtn) {
        const handler = () => this._scrollByCard(1);
        this.nextBtn.addEventListener('click', handler);
        this._listeners.push(() => this.nextBtn.removeEventListener('click', handler));
      }

      const keyHandler = (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._scrollByCard(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._scrollByCard(1);
        }
      };
      this.track.addEventListener('keydown', keyHandler);
      this._listeners.push(() => this.track.removeEventListener('keydown', keyHandler));

      const scrollHandler = () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(() => {
          this._updateArrowStates();
          this._scrollRaf = null;
        });
      };
      this.track.addEventListener('scroll', scrollHandler, { passive: true });
      this._listeners.push(() => this.track.removeEventListener('scroll', scrollHandler));

      const resizeHandler = () => this._updateArrowStates();
      window.addEventListener('resize', resizeHandler, { passive: true });
      this._listeners.push(() => window.removeEventListener('resize', resizeHandler));
    }

    _scrollByCard(direction) {
      if (!this.slides[0]) return;
      const cardWidth = this.slides[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({
        left: direction * (cardWidth + gap),
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    _updateArrowStates() {
      if (!this.prevBtn && !this.nextBtn) return;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    }

    _observeEntry() {
      if (this.reducedMotion || typeof IntersectionObserver === 'undefined') {
        this.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-visible');
              observer.disconnect();
            }
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(this);
      this._observers.push(observer);
    }

    _observeSlides() {
      if (!this.counterCurrent || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver(
        (entries) => {
          let bestEntry = null;
          entries.forEach((entry) => {
            if (entry.isIntersecting && (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio)) {
              bestEntry = entry;
            }
          });
          if (bestEntry) {
            const idx = this.slides.indexOf(bestEntry.target);
            if (idx >= 0) {
              this.counterCurrent.textContent = String(idx + 1).padStart(2, '0');
            }
          }
        },
        { root: this.track, threshold: [0.5, 0.75, 1] }
      );
      this.slides.forEach((slide) => observer.observe(slide));
      this._observers.push(observer);
    }
  }

  customElements.define('pb-shipping-slider', PbShippingSlider);
}
